import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  MapPin,
  Calendar,
  DollarSign,
  Activity,
  Building,
  AlertTriangle,
  FileText,
  TrendingUp
} from 'lucide-react';
import { Work, WorkStage, GrantLevel, CategoryPST, ModalProps } from '../../types/work.types';
import { worksApiService } from '../../services/workApi.service';

interface WorkDetailsProps extends ModalProps {
  workId: number;
}

const WorkDetails: React.FC<WorkDetailsProps> = ({ workId, isOpen, onClose }) => {
  const [work, setWork] = useState<Work | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWorkDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await worksApiService.getWorkById(workId);
      setWork(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load work details');
    } finally {
      setLoading(false);
    }
  }, [workId]);

  useEffect(() => {
    if (isOpen) {
      fetchWorkDetails();
    }
  }, [isOpen, fetchWorkDetails]);

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          </div>
          <p className="text-center text-gray-600 mt-4">Loading work details...</p>
        </div>
      </div>
    );
  }

  if (error || !work) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Error</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-red-500 mr-2" />
            <p className="text-red-600">{error || 'Work not found'}</p>
          </div>
          <button
            onClick={fetchWorkDetails}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const progress = worksApiService.calculateProgress(work);
  const delayed = worksApiService.isWorkDelayed(work);
  const stageColors = worksApiService.getStageColor(work.abstract_stage_of_work);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Work Details</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Work Information */}
        <div className="space-y-6">
          {/* Work Name and SNO */}
          <div>
            <h3 className="text-lg font-medium text-gray-900">{work.work_name}</h3>
            <p className="text-sm text-gray-600">SNO: {work.sno} {delayed && <AlertTriangle className="w-4 h-4 inline text-red-500 ml-2" />}</p>
          </div>

          {/* Agency and Grant Level */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center">
              <Building className="w-5 h-5 text-gray-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-700">Agency</p>
                <p className="text-gray-900">{work.agency_name}</p>
              </div>
            </div>
            <div className="flex items-center">
              <FileText className="w-5 h-5 text-gray-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-700">Grant Level</p>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  work.oas_grant_level === GrantLevel.CENTRAL 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {work.oas_grant_level}
                </span>
              </div>
            </div>
          </div>

          {/* Cost Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center">
              <DollarSign className="w-5 h-5 text-gray-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-700">Estimated Cost</p>
                <p className="text-gray-900">{worksApiService.formatCurrency(work.estimate_cost)}</p>
              </div>
            </div>
            {work.ras_amount && work.ras_amount !== work.estimate_cost && (
              <div className="flex items-center">
                <DollarSign className="w-5 h-5 text-gray-500 mr-2" />
                <div>
                  <p className="text-sm font-medium text-gray-700">RAS Amount</p>
                  <p className="text-gray-900">{worksApiService.formatCurrency(work.ras_amount)}</p>
                </div>
              </div>
            )}
            <div className="flex items-center">
              <DollarSign className="w-5 h-5 text-gray-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-700">Expenditure</p>
                <p className="text-gray-900">{worksApiService.formatCurrency(work.cum_exp_on_work)}</p>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="flex items-center">
            <MapPin className="w-5 h-5 text-gray-500 mr-2" />
            <div>
              <p className="text-sm font-medium text-gray-700">Location</p>
              <p className="text-gray-900">
                {work.district?.name || 'N/A'} {work.mandal?.name ? `, ${work.mandal.name}` : ''}
                {work.length_sanctioned_km && ` (${work.length_sanctioned_km} km)`}
              </p>
            </div>
          </div>

          {/* Progress */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Progress</p>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Financial Progress</span>
                  <span className="font-medium">{progress.financial}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div
                    className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress.financial}%` }}
                  ></div>
                </div>
              </div>
              {work.length_sanctioned_km && work.length_sanctioned_km > 0 && (
                <div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Physical Progress</span>
                    <span className="font-medium">{progress.physical}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                    <div
                      className="bg-gradient-to-r from-blue-400 to-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress.physical}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Stage and Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center">
              <TrendingUp className="w-5 h-5 text-gray-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-700">Stage</p>
                <div className="space-y-1">
                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${stageColors.bg} ${stageColors.text}`}>
                    {work.abstract_stage_of_work}
                  </span>
                  {work.sub_stage_of_work && (
                    <p className="text-xs text-gray-600">{work.sub_stage_of_work}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center">
              <Calendar className="w-5 h-5 text-gray-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-700">Dates</p>
                <p className="text-gray-900">Sanctioned: {worksApiService.formatDate(work.oas_sanction_date)}</p>
                {work.completion_date_set && (
                  <p className={`text-sm ${delayed ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                    Due: {worksApiService.formatDate(work.completion_date_set)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Category */}
          <div className="flex items-center">
            <FileText className="w-5 h-5 text-gray-500 mr-2" />
            <div>
              <p className="text-sm font-medium text-gray-700">Category</p>
              <p className="text-gray-900">
                {work.category === 'P' ? 'Plain' : work.category === 'S' ? 'SCP' : work.category === 'T' ? 'TSP' : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkDetails;